import { useParams, Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { IconArrowLeft } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import { useClient } from '@/stores/useClient.ts'
import { CustomPriceList, CustomPriceListItem } from '@/models/priceList.ts'
import LoadBig from '@/components/LoadBig/LoadBig.tsx'
import InlineEditInput from '@/components/portal/InlineEditInput.tsx'
import InlinePriceInput from '@/components/portal/InlinePriceInput.tsx'

function formatEuro(value: string): string {
  return parseFloat(value).toFixed(2).replace('.', ',') + ' €'
}

function getItemCostPrice(item: CustomPriceListItem): string {
  return item.variation_detail?.cost_base_price ?? item.product_detail.cost_base_price
}

function getItemCostUnitPrice(item: CustomPriceListItem): string | null {
  return item.variation_detail?.cost_unit_price ?? item.product_detail.cost_unit_price
}

function calcMargin(sell: string, cost: string): string {
  return (parseFloat(sell) - parseFloat(cost)).toFixed(2)
}

function isPhotobook(item: CustomPriceListItem): boolean {
  return item.product_detail.sku === 'photobook'
}

export default function PriceListDetailPage() {
  const { priceListId } = useParams<{ priceListId: string }>()
  const queryClient = useQueryClient()
  const { getPriceList, updatePriceList, updatePriceListItem } = useClient()

  const { data, isLoading, error } = useQuery({
    queryKey: ['priceList', priceListId],
    queryFn: async () => {
      const res = await getPriceList(priceListId!)
      return res.data as CustomPriceList
    },
    enabled: !!priceListId,
  })

  async function handleNameChange(newName: string) {
    await updatePriceList(priceListId!, { name: newName })
    queryClient.invalidateQueries({ queryKey: ['priceList', priceListId] })
    queryClient.invalidateQueries({ queryKey: ['priceLists'] })
    toast('Name aktualisiert', { autoClose: 2000 })
  }

  async function handleItemUpdate(item: CustomPriceListItem, field: 'base_price' | 'unit_price' | 'enabled_for_sell', value: string | null | boolean) {
    try {
      await updatePriceListItem(priceListId!, item.id, { [field]: value })
      queryClient.invalidateQueries({ queryKey: ['priceList', priceListId] })
    } catch (e: any) {
      console.error('Error updating item:', e)
      toast.error('Fehler beim Aktualisieren')
    }
  }

  if (isLoading) return <LoadBig />

  if (error || !data) {
    return (
      <div className="container mx-auto p-4">
        <p className="text-error">Fehler beim Laden der Preisliste</p>
      </div>
    )
  }

  const priceList = data

  // Group items by product, product-level item (no variation) first
  const grouped = new Map<string, CustomPriceListItem[]>()
  for (const item of priceList.items) {
    const key = item.product_detail.id
    if (!grouped.has(key)) grouped.set(key, [])
    grouped.get(key)!.push(item)
  }
  for (const items of grouped.values()) {
    items.sort((a, b) => {
      if (!a.variation_detail && b.variation_detail) return -1
      if (a.variation_detail && !b.variation_detail) return 1
      return 0
    })
  }

  return (
    <div className="container mx-auto p-4">
      <Link to="/portal/shop" className="inline-flex items-center gap-1 text-sm opacity-60 hover:opacity-100 transition-opacity mb-4">
        <IconArrowLeft size={16} />
        Preislisten
      </Link>

      <div className="mb-6">
        <InlineEditInput value={priceList.name} onSave={handleNameChange}>
          <h1 className="text-2xl font-bold">{priceList.name}</h1>
        </InlineEditInput>
      </div>

      {priceList.items.length === 0 ? (
        <div className="bg-secondary p-8 text-center">
          <p className="opacity-60">Keine Produkte in dieser Preisliste.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>Produkt / Variante</th>
                <th className="text-right">EK (Brutto)</th>
                <th className="text-right">VK (Brutto)</th>
                <th className="text-right">Gewinn</th>
                <th className="text-center">Aktiv</th>
              </tr>
            </thead>
            <tbody>
              {[...grouped.entries()].map(([productId, items]) => (
                <ProductGroup
                  key={productId}
                  items={items}
                  onItemUpdate={handleItemUpdate}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

interface ProductGroupProps {
  items: CustomPriceListItem[]
  onItemUpdate: (item: CustomPriceListItem, field: 'base_price' | 'unit_price' | 'enabled_for_sell', value: string | null | boolean) => void
}

function ProductGroup({ items, onItemUpdate }: ProductGroupProps) {
  const productTitle = items[0].product_detail.title
  const productItem = items.find(i => !i.variation_detail)
  const variationItems = items.filter(i => i.variation_detail != null)
  const hasVariations = variationItems.length > 0
  const productDisabled = productItem != null && !productItem.enabled_for_sell
  const unitLabel = isPhotobook(items[0]) ? 'Seite' : 'Stk.'

  const previewImage = items[0].product_detail.preview_image

  if (hasVariations) {
    // Product with variations: header row (name + toggle only), then variation rows with prices
    return (
      <>
        {productItem && (
          <tr className="bg-secondary">
            <td className="font-semibold">
              <LabelWithImage label={productTitle} image={previewImage} />
            </td>
            <td />
            <td />
            <td />
            <td className="text-center">
              <input
                type="checkbox"
                className="toggle toggle-primary toggle-sm"
                checked={productItem.enabled_for_sell}
                onChange={(e) => onItemUpdate(productItem, 'enabled_for_sell', e.target.checked)}
              />
            </td>
          </tr>
        )}
        {variationItems.map(item => (
          <ItemRow
            key={item.id}
            item={item}
            label={item.variation_detail!.full_title}
            image={previewImage}
            unitLabel={unitLabel}
            indent={true}
            disabled={productDisabled || !item.enabled_for_sell}
            productDisabled={productDisabled}
            onItemUpdate={onItemUpdate}
          />
        ))}
      </>
    )
  }

  // Product without variations: single row with all columns
  if (!productItem) return null
  return (
    <ItemRow
      item={productItem}
      label={productTitle}
      image={previewImage}
      unitLabel={unitLabel}
      indent={false}
      disabled={false}
      productDisabled={false}
      onItemUpdate={onItemUpdate}
    />
  )
}

function LabelWithImage({ label, image }: { label: string, image: string }) {
  return (
    <div className="flex items-center gap-3">
      {image && (
        <img
          src={image}
          alt=""
          className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
        />
      )}
      <span>{label}</span>
    </div>
  )
}

interface ItemRowProps {
  item: CustomPriceListItem
  label: string
  image: string
  unitLabel: string
  indent: boolean
  disabled: boolean
  productDisabled: boolean
  onItemUpdate: (item: CustomPriceListItem, field: 'base_price' | 'unit_price' | 'enabled_for_sell', value: string | null | boolean) => void
}

function PriceCell({ value, unitValue, unitLabel }: { value: string, unitValue?: string | null, unitLabel: string }) {
  return (
    <div className="text-right">
      <div>{formatEuro(value)}</div>
      {unitValue && (
        <div className="mt-1.5">
          <div>+ {formatEuro(unitValue)}</div>
          <div className="text-xs opacity-50">pro {unitLabel}</div>
        </div>
      )}
    </div>
  )
}

function EditablePriceCell({ item, unitLabel, disabled, onItemUpdate }: {
  item: CustomPriceListItem
  unitLabel: string
  disabled: boolean
  onItemUpdate: (item: CustomPriceListItem, field: 'base_price' | 'unit_price' | 'enabled_for_sell', value: string | null | boolean) => void
}) {
  const hasUnit = item.unit_price != null
  return (
    <div className="text-right">
      <div>
        <InlinePriceInput
          value={item.base_price}
          onSave={(v) => onItemUpdate(item, 'base_price', v)}
          disabled={disabled}
        />
      </div>
      {hasUnit && (
        <div className="mt-1.5">
          <div>
            <InlinePriceInput
              value={item.unit_price}
              onSave={(v) => onItemUpdate(item, 'unit_price', v)}
              disabled={disabled}
              prefix="+"
            />
          </div>
          <div className="text-xs opacity-50">pro {unitLabel}</div>
        </div>
      )}
    </div>
  )
}

function ItemRow({ item, label, image, unitLabel, indent, disabled, productDisabled, onItemUpdate }: ItemRowProps) {
  const costPrice = getItemCostPrice(item)
  const costUnitPrice = getItemCostUnitPrice(item)
  const hasUnit = item.unit_price != null
  const margin = calcMargin(item.base_price, costPrice)
  const unitMargin = hasUnit && costUnitPrice ? calcMargin(item.unit_price!, costUnitPrice) : null

  return (
    <tr className="hover">
      <td>
        <div className={`flex items-center gap-3 ${indent ? 'pl-4' : ''}`}>
          {image && (
            <img
              src={image}
              alt=""
              className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
            />
          )}
          <span>{label}</span>
          {productDisabled && (
            <span className="badge badge-sm badge-warning ml-2">Produkt deaktiviert</span>
          )}
        </div>
      </td>
      <td className="text-right">
        <PriceCell value={costPrice} unitValue={costUnitPrice} unitLabel={unitLabel} />
      </td>
      <td className="text-right">
        <EditablePriceCell item={item} unitLabel={unitLabel} disabled={disabled} onItemUpdate={onItemUpdate} />
      </td>
      <td className="text-right">
        <PriceCell value={margin} unitValue={unitMargin} unitLabel={unitLabel} />
      </td>
      <td className="text-center">
        <input
          type="checkbox"
          className="toggle toggle-primary toggle-sm"
          checked={item.enabled_for_sell}
          onChange={(e) => onItemUpdate(item, 'enabled_for_sell', e.target.checked)}
        />
      </td>
    </tr>
  )
}
